import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import { Button, Divider, Form, Input, InputNumber, Select, Checkbox, App, Modal, Typography, Alert, Tooltip, Switch, } from 'antd';
import { EyeOutlined, EyeInvisibleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import FormModal from 'components/FormModal';
import LanguageSelect from 'components/LanguageSelect';
import { createUser, addUserClaims, fetchAllTeamOwnerUsers, fetchAllUsersSimplified, } from 'api/admin/users-api';
import { fetchPasswordSettings } from 'api/user-api';
import { addTeamMember } from 'api/admin/teams-api';
import { getUiLocales, getCurrentUiLocale } from 'libs/locales/ui';
import { txt } from 'libs/i18n';
import { useSession } from 'components/Authenticated';
import { filterOption } from 'libs/filterOption';
import { regexThousandsSeparator } from '../../components/CreditSettings';
import styles from './style.module.less';
const { Option } = Select;
const NewUserForm = ({ onCreate, onCancel, modalVisible, }) => {
    const [form] = Form.useForm();
    const sendEmailChecked = Form.useWatch('sendEmail', form);
    const { session } = useSession();
    const { message } = App.useApp();
    const history = useHistory();
    const [isEmailTaken, setIsEmailTaken] = useState(false);
    const [userEmails, setUserEmails] = useState([]);
    const [teamOwnerUsers, setTeamOwnerUsers] = useState('loading');
    const [addedToTeam, setAddedToTeam] = useState(false);
    const [setupTokenLink, setSetupTokenLink] = useState(null);
    const [passwordSettings, setPasswordSettings] = useState('loading');
    const [submitLoading, setSubmitLoading] = useState(false);
    const unlimitedCredit = session.login.hasClaim('dataTheme:msp');
    useEffect(() => {
        if (modalVisible) {
            void (async () => {
                const [usersList, pwdSettings] = await Promise.all([
                    fetchAllUsersSimplified(session.connection),
                    fetchPasswordSettings(),
                ]);
                setUserEmails(usersList.list.map(({ email }) => email));
                setTeamOwnerUsers(await fetchAllTeamOwnerUsers(session.connection));
                setPasswordSettings(pwdSettings);
            })();
        }
    }, [modalVisible]);
    const handleIsEmailRegistered = ({ target: { value } }) => {
        const trimmedEmail = value.trim();
        setIsEmailTaken(userEmails.some((email) => (email === null || email === void 0 ? void 0 : email.toLowerCase()) === trimmedEmail.toLowerCase()));
    };
    const reportOnSubmitError = (error) => {
        let loggerError;
        let messageError;
        let extraText = '';
        switch (error) {
            case 'short-password':
                loggerError = 'Low password complexity';
                messageError = 'minCharsPsw';
                if (passwordSettings !== 'loading') {
                    extraText = String(passwordSettings.requiredLength);
                }
                break;
            case 'missing-credit':
                loggerError = 'Missing number of credits.';
                messageError = 'missingCredit';
                break;
            case 'invalid-email':
                loggerError = 'Invalid email';
                messageError = 'pleaseEmail';
                break;
            case 'duplicit-email':
                loggerError = 'Duplicate email';
                messageError = 'alreadyRegistered';
                break;
            default:
                loggerError = 'Creating user failed';
                messageError = 'failedToCreate';
        }
        global.logger.error(loggerError);
        void message.error(txt(messageError) + extraText);
    };
    const handleAddToTeam = (e) => {
        setAddedToTeam(e.target.checked);
        if (e.target.checked) {
            form.setFieldsValue({ credit: 0 });
        }
    };
    const handleSubmit = async (values) => {
        const { email, role, credit, tutorial, sendEmail, uiLocale, teamId, } = values;
        setSubmitLoading(true);
        try {
            const result = addedToTeam
                ? await addTeamMember(session.connection, email, role, tutorial, sendEmail, uiLocale, teamId) : await createUser(session.connection, email, role, credit, tutorial, sendEmail, uiLocale);
            if (result.isSuccess()) {
                const response = result.get();
                await addUserClaims(session.connection, response.id, ['paidFeatures:enabled']);
                void message.success(txt('newUserCreated'), 2);
                form.resetFields();
                const { passwordSetupToken } = response.password;
                if (sendEmail === false && passwordSetupToken !== undefined && passwordSetupToken !== '') {
                    setSetupTokenLink({
                        token: passwordSetupToken,
                        userId: response.id,
                        revealed: false,
                    });
                }
                else {
                    onCreate();
                    history.push(`/admin/users/edituser/${response.id}`);
                }
            }
            else {
                reportOnSubmitError(result.err());
            }
        }
        finally {
            setSubmitLoading(false);
        }
    };
    return (_jsxs(FormModal, { open: modalVisible, footer: [
            _jsx(Button, { type: "default", onClick: onCancel, children: txt('cancel') }, "back"),
            _jsx(Button, { type: "primary", htmlType: "submit", loading: submitLoading, onClick: () => form.submit(), children: txt('create') }, "submit")
        ], onCancel: onCancel, title: txt('newUser'), children: [setupTokenLink !== null && (_jsxs(Modal, { title: txt('setPasswordTokenTitle'), open: true, footer: [
                    _jsx(Button, { type: "primary", onClick: () => {
                            const { userId } = setupTokenLink;
                            setSetupTokenLink(null);
                            onCreate();
                            history.push(`/admin/users/edituser/${userId}`);
                        }, children: txt('close') }, "close"),
                ], onCancel: () => {
                    const { userId } = setupTokenLink;
                    setSetupTokenLink(null);
                    onCreate();
                    history.push(`/admin/users/edituser/${userId}`);
                }, children: [_jsx(Alert, { type: "warning", message: txt('setPasswordTokenWarning'), showIcon: true }), _jsx("p", { className: styles.setupTokenInstructions, children: txt('setPasswordTokenInstructions') }), _jsxs("div", { className: styles.setupTokenRow, children: [_jsx(Typography.Paragraph, { className: styles.setupTokenParagraph, copyable: setupTokenLink.revealed
                                    ? { tooltips: [txt('copyToClipboard'), txt('textCopied')] }
                                    : false, children: setupTokenLink.revealed
                                    ? `${window.location.origin}/login/setpassword#token=${encodeURIComponent(setupTokenLink.token)}`
                                    : '••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••' }), _jsx(Tooltip, { title: txt('revealToken'), children: _jsx(Button, { type: "text", icon: setupTokenLink.revealed ? _jsx(EyeInvisibleOutlined, {}) : _jsx(EyeOutlined, {}), onClick: () => {
                                        setSetupTokenLink((prev) => (prev !== null ? Object.assign(Object.assign({}, prev), { revealed: !prev.revealed }) : null));
                                    } }) })] })] })), _jsxs(Form, { initialValues: {
                    role: 'Standard',
                    credit: unlimitedCredit ? 10000000 : 0,
                    tutorial: true,
                    sendEmail: true,
                    uiLocale: getCurrentUiLocale(),
                }, form: form, onFinish: handleSubmit, className: styles.userForm, children: [_jsx(Form.Item, { label: txt('email'), name: "email", rules: [
                            {
                                required: true,
                                message: txt('pleaseEmail'),
                                transform: (value) => value.trim(),
                                type: 'email',
                            },
                            {
                                max: 254,
                                message: txt('maxCharacters'),
                            },
                            {
                                validator: async (_, value) => {
                                    if (value !== '' && isEmailTaken) {
                                        return Promise.reject(txt('alreadyRegistered'));
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ], children: _jsx(Input, { onChange: handleIsEmailRegistered, autoFocus: true }) }), _jsx(Form.Item, { name: "role", label: txt('role'), rules: [{ required: true }], children: _jsxs(Select, { children: [_jsx(Option, { value: "Enterprise", children: "Enterprise" }, "enterprise"), _jsx(Option, { value: "Standard", children: "Standard" }, "standard"), _jsx(Option, { value: "Admin", children: "Admin" }, "admin"), _jsx(Option, { value: "Developer", children: "Developer" }, "developer")] }) }), _jsx(Form.Item, { label: txt('addToTeam'), children: _jsx(Checkbox, { onChange: handleAddToTeam }) }), addedToTeam && teamOwnerUsers !== 'loading' ? (_jsx(Form.Item, { name: "teamId", label: `${txt('chooseTeam')}:`, rules: [
                            {
                                required: true,
                                message: txt('chooseTeam'),
                            },
                        ], children: _jsx(Select, { placeholder: txt('teamOwner'), showSearch: true, filterOption: filterOption, children: teamOwnerUsers.map((user) => (_jsx(Option, { value: user.teamId, label: user.email, children: user.email }, user.teamId))) }) })) : null, _jsx(Form.Item, { name: "credit", label: txt('creditMinutes'), tooltip: addedToTeam ? { title: txt('sharedCredit') } : null, rules: [
                            {
                                required: true,
                                type: 'number',
                                min: 0,
                                max: unlimitedCredit ? 10000000 : 100000,
                                message: `${txt('enterCredit')} ${unlimitedCredit ? '10 000 000' : '100 000'}`,
                            },
                        ], children: _jsx(InputNumber, { className: styles.creditInput, precision: 0, disabled: addedToTeam, formatter: (value) => (value === undefined ? String(0) : value.toString().replace(regexThousandsSeparator, ' ')), 
                            // NOTE: Parse back to number and remove spaces. spaces
                            parser: (value) => (value === undefined ? 0 : Number(value.replace(/\$\s?|( *)/g, ''))) }) }), _jsx(Divider, {}), _jsx(Form.Item, { className: styles.checkboxes, label: txt('addBeeyTutorial'), name: "tutorial", valuePropName: "checked", children: _jsx(Checkbox, {}) }), _jsx(Form.Item, { className: styles.checkboxes, label: txt('infoEmail'), name: "sendEmail", valuePropName: "checked", tooltip: {
                            title: sendEmailChecked === true ? txt('sendEmailInfoChecked') : txt('sendEmailInfoUnchecked'),
                            icon: _jsx(InfoCircleOutlined, {}),
                        }, children: _jsx(Switch, { size: "small" }) }), _jsx(Form.Item, { name: "uiLocale", label: txt('language'), children: _jsx(LanguageSelect, { locales: getUiLocales() }) })] })] }));
};
export default NewUserForm;
